import React, { useEffect, useState } from 'react';
import { ArrowLeft, Search, User, Phone, MapPin, Calendar, Clock, Users, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import axiosInstance from '../api/axiosInstance';
import InactivityTimer from '../components/InactivityTimer';

const OnlinePayment = () => {
    const navigate = useNavigate();
    const [mrno, setMrno] = useState('');
    const [patientData, setPatientData] = useState(null);
    const [patientList, setPatientList] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState({
        // bed: [],
        lab: [],
        imaging: [],
        ot: []
    });
    const [activeTab, setActiveTab] = useState('all');
    const [cart, setCart] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showPaymentStatusModal, setShowPaymentStatusModal] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentMessage, setPaymentMessage] = useState('');
    const [showIPStatusDialog, setShowIPStatusDialog] = useState(false);
    const [isBlockedIPPatient, setIsBlockedIPPatient] = useState(false);
    const [visitDetails, setVisitDetails] = useState(null);

    const serviceTypes = {
        // bed: 117,
        lab: 115,
        imaging: 116,
        ot: 1012
    };

    // Fetch patient visit details to check IP status
    const fetchPatientVisitDetails = async (mrno) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(mrno)}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                },
            });

            if (response.data && response.data.status === 'success' && response.data.data && response.data.data[0]) {
                const visitData = response.data.data[0].visitDetails;
                setVisitDetails(visitData);

                // Block payment if patient status starts with 'IP' AND visitStatus is 'open'
                if (visitData && visitData.patVisitStatus && visitData.patVisitStatus.startsWith('IP') && visitData.visitStatus === 'open') {
                    setIsBlockedIPPatient(true);
                    setShowIPStatusDialog(true);
                } else {
                    setIsBlockedIPPatient(false);
                }
            }
        } catch (err) {
            console.error('Error fetching visit details:', err);
        }
    };

    const fetchServices = async (type, serviceTypeId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/serviceorder/master-with-tariff?serviceType=${serviceTypeId}&siteId=${localStorage.getItem('site_id')}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.data.status === 'success') {
                setServices(prev => ({ ...prev, [type]: response.data.data }));
            } else {
                console.error(`Failed to fetch ${type} services:`, response.data.message);
            }
        } catch (err) {
            console.error(`Error fetching ${type} services:`, err);
        }
    };

    const fetchSearchResults = async (term) => {
        if (term.length < 2) {
            setSearchResults([]);
            setSearchLoading(false);
            return;
        }

        setSearchLoading(true);
        try {
            const url = activeTab === 'all'
                ? `${process.env.REACT_APP_KIOSK_OPS_URL}/serviceorder/master-with-tariff?serviceName=${encodeURIComponent(term)}&siteId=${localStorage.getItem('site_id')}`
                : `${process.env.REACT_APP_KIOSK_OPS_URL}/serviceorder/master-with-tariff?serviceType=${serviceTypes[activeTab]}&serviceName=${encodeURIComponent(term)}&siteId=${localStorage.getItem('site_id')}`;

            const response = await axios.get(url, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.data.status === 'success') {
                setSearchResults(response.data.data.map(item => ({
                    ...item,
                    category: activeTab === 'all' ? item.serviceGroup : activeTab.charAt(0).toUpperCase() + activeTab.slice(1) + ' Charges'
                })));
            } else {
                setSearchResults([]);
                console.error('Failed to fetch search results:', response.data.message);
            }
        } catch (err) {
            setSearchResults([]);
            console.error('Error fetching search results:', err);
        } finally {
            setSearchLoading(false);
        }
    };

    useEffect(() => {
        const initializeApp = async () => {
            try {
                const token = localStorage.getItem("token");

                // Now fetch all services after token is stored
                if (token) {
                    await Promise.all([
                        // fetchServices('bed', serviceTypes.bed),
                        fetchServices('lab', serviceTypes.lab),
                        fetchServices('imaging', serviceTypes.imaging),
                        fetchServices('ot', serviceTypes.ot)
                    ]);
                }
            } catch (error) {
                console.error("Error validating user:", error);
            }
        };

        initializeApp();
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (patientData) {
                fetchSearchResults(searchTerm);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, activeTab, patientData]);

    const handleInputChange = (e) => {
        setMrno(e.target.value);
        setError('');
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleFetchDetails = async () => {
        if (!mrno) {
            setError('Please enter a valid MRN or mobile number');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(mrno)}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.data.status === 'success' && Array.isArray(response.data.data) && response.data.data.length > 0) {
                if (response.data.data.length === 1) {
                    const patient = response.data.data[0];

                    // First check visit details before setting patient data
                    const visitResponse = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(patient.mrno)}`, {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                        },
                    });

                    if (visitResponse.data && visitResponse.data.status === 'success' && visitResponse.data.data && visitResponse.data.data[0]) {
                        const visitData = visitResponse.data.data[0].visitDetails;
                        setVisitDetails(visitData);

                        // Check if patient is IP with open visit status
                        if (visitData && visitData.patVisitStatus && visitData.patVisitStatus.startsWith('IP') && visitData.visitStatus === 'open') {
                            setIsBlockedIPPatient(true);
                            setShowIPStatusDialog(true);
                            // Don't set patient data to prevent loading services
                            setLoading(false);
                            return;
                        }
                    }

                    // If not blocked, set patient data and load services
                    setPatientData({
                        prefix: patient.prefix || '',
                        firstName: patient.firstName || '',
                        middleName: patient.middleName || '',
                        lastName: patient.lastName || '',
                        mrno: patient.mrno || '',
                        genderCode: patient.genderCode || '',
                        dob: patient.dobStr ? patient.dobStr.split('-').reverse().join('-') : '',
                        age: patient.age || '',
                        ageSelect: 'year',
                        contactNumber: patient.contactNo || '',
                        emailAddress: patient.email || '',
                        emergencyContactName: patient.emergencyContactName || '',
                        emergencyContactNo: patient.emergencyContactNo || patient.contactNo || '',
                        city: patient.addressList?.[0]?.cityName || '',
                        district: patient.addressList?.[0]?.districtName || '',
                        state: patient.addressList?.[0]?.stateName || '',
                        nationality: patient.addressList?.[0]?.countryName || '',
                        addressList: patient.addressList || [],
                        base64String: patient.photo || null
                    });
                    setPatientList([]);
                    setIsBlockedIPPatient(false);
                } else {
                    setPatientList(response.data.data);
                    setPatientData(null);
                }
                setError('');
            } else {
                setError('No patient found for this MRN or mobile number');
                setPatientData(null);
                setPatientList([]);
            }
        } catch (err) {
            setError('Failed to fetch patient details');
            setPatientData(null);
            setPatientList([]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleFetchDetails();
        }
    };

    const addToCart = (service) => {
        setCart(prev => {
            const existingService = prev.find(item => item.serviceId === service.serviceId);
            if (existingService) {
                return prev.map(item =>
                    item.serviceId === service.serviceId
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prev, { ...service, quantity: 1 }];
        });
    };

    useEffect(() => {
        const total = cart.reduce((sum, item) => sum + (item.hospitalTariff * item.quantity), 0);
        setTotalAmount(total);
    }, [cart]);

    const removeFromCart = (serviceId) => {
        setCart(prev => prev.filter(item => item.serviceId !== serviceId));
    };

    const updateQuantity = (serviceId, delta) => {
        setCart(prev =>
            prev.map(item =>
                item.serviceId === serviceId
                    ? { ...item, quantity: Math.max(1, item.quantity + delta) }
                    : item
            )
        );
    };

    const handlePayment = async () => {
        // Block payment if patient is IP with open visit status
        if (isBlockedIPPatient) {
            setShowIPStatusDialog(true);
            return;
        }

        if (cart.length === 0) {
            setPaymentStatus('failure');
            setPaymentMessage('Please add services to the cart before proceeding to payment.');
            setShowPaymentStatusModal(true);
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.post('/api/patient/create-service-order', {
                token: localStorage.getItem('token'),
                patientMrno: patientData.mrno,
                services: cart.map(item => ({
                    serviceId: item.serviceId,
                    serviceName: item.serviceName,
                    hospitalTariff: item.hospitalTariff,
                    quantity: item.quantity
                })),
                totalAmount,
                siteCode: localStorage.getItem('site_code') || null,
            }, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('kiosk_token')}` }
            });

            const { key, amount, currency, order_id } = response.data;

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://api.razorpay.com/v1/checkout/embedded';

            const formFields = {
                key_id: key,
                amount: amount.toString(),
                currency,
                order_id,
                name: "KIMS",
                description: "Service Payment",
                image: "/logo.jpg",
                callback_url: `${axiosInstance.defaults.baseURL}/api/patient/verify-payment`,
                cancel_url: `${axiosInstance.defaults.baseURL}/api/patient/cancel-payment`,
                prefill: {
                    name: `${patientData.firstName || ''}  ${patientData.middleName || ''} ${patientData.lastName || ''}`.trim() || 'Test User',
                    email: patientData.emailAddress || 'test@gmail.com',
                    contact: patientData.contactNumber || '9427388748',
                },
                theme: { color: "#0FA968" },
                notes: {
                    service_order: true, // Add this to identify service orders
                },
            };

            Object.keys(formFields).forEach((key) => {
                if (typeof formFields[key] === 'object') {
                    Object.keys(formFields[key]).forEach((subKey) => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = `${key}[${subKey}]`;
                        input.value = formFields[key][subKey];
                        form.appendChild(input);
                    });
                } else {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = formFields[key];
                    form.appendChild(input);
                }
            });

            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);
        } catch (err) {
            setPaymentStatus('failure');
            setPaymentMessage('Error creating order: ' + (err.response?.data?.error || err.message || 'Unknown error'));
            setShowPaymentStatusModal(true);
            console.error('Error creating order:', err);
        } finally {
            setLoading(false);
        }
    };

    const allServices = [
        // ...services.bed.map(item => ({ ...item, category: 'Bed Charges' })),
        ...services.lab.map(item => ({ ...item, category: 'Lab Charges' })),
        ...services.imaging.map(item => ({ ...item, category: 'Imaging Charges' })),
        ...services.ot.map(item => ({ ...item, category: 'OT Charges' }))
    ];

    const displayedServices = searchTerm.length >= 2 ? searchResults : (activeTab === 'all' ? allServices : services[activeTab]);

    const renderPaymentStatusModal = () => {
        if (!paymentStatus || paymentStatus === 'success') return null;
        const statusConfig = {
            failure: { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: "❌", title: "Payment Failed" },
            cancel: { bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-800", icon: "⚠️", title: "Payment Cancelled" }
        };
        const config = statusConfig[paymentStatus];
        return (
            <div className="fixed inset-0 z-50 flex justify-center w-full h-full bg-black bg-opacity-50 overflow-y-auto p-2 sm:p-4">
                <div className="relative my-auto p-4 sm:p-6 w-full max-w-lg bg-white rounded-2xl shadow-2xl">
                    <button
                        onClick={() => setShowPaymentStatusModal(false)}
                        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div className={`p-6 border-2 rounded-xl ${config.bg} ${config.border} text-center`}>
                        <span className="text-4xl mb-4 block">{config.icon}</span>
                        <h3 className={`font-bold text-xl ${config.text}`}>{config.title}</h3>
                        <p className={`mt-2 ${config.text} opacity-90`}>{paymentMessage}</p>
                        <div className="mt-6 flex justify-center gap-4">
                            <button
                                onClick={() => {
                                    setShowPaymentStatusModal(false);
                                    setPaymentStatus(null);
                                    setPaymentMessage("");
                                }}
                                className="px-6 py-2 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => setShowPaymentStatusModal(false)}
                                className="px-6 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg shadow hover:bg-gray-300 transition duration-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
                <div className="bg-white shadow-sm border-b">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between py-4">
                            <div className="flex items-center space-x-4">
                                <Link to="/" className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors duration-200 shadow-sm">
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Back
                                </Link>
                                <div className="hidden sm:block w-px h-8 bg-gray-300"></div>
                                <h1 className="text-xl font-semibold text-gray-900">Book Service</h1>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {!patientData && patientList.length === 0 && (
                        <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <label htmlFor="mrno" className="block text-sm font-medium text-gray-700 mb-2">
                                        MRN / Mobile Number
                                    </label>
                                    <input
                                        type="text"
                                        id="mrno"
                                        value={mrno}
                                        onChange={handleInputChange}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Enter MRN or mobile number to search patient details"
                                        className={`w-full px-4 py-3 border-2 rounded-lg transition-colors duration-200 ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-emerald-500 focus:ring-emerald-200'
                                            } focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                        aria-invalid={error ? 'true' : 'false'}
                                        aria-describedby="mrno-error"
                                    />
                                </div>
                                <div className="flex items-end">
                                    <button
                                        onClick={handleFetchDetails}
                                        disabled={loading}
                                        className={`px-8 py-3 bg-emerald-600 text-white font-medium rounded-lg shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50 transition-all duration-200 ${loading ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                    >
                                        {loading ? (
                                            <div className="flex items-center">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                Searching...
                                            </div>
                                        ) : (
                                            <div className="flex items-center">
                                                <Search className="w-4 h-4 mr-2" />
                                                Get Details
                                            </div>
                                        )}
                                    </button>
                                </div>
                            </div>
                            {error && (
                                <div className="mt-4 flex items-center p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                                    <p className="text-red-700 text-sm">{error}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {patientList.length > 1 && (
                        <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-gray-800">Select Patient Profile</h2>
                                <button
                                    onClick={() => { setPatientList([]); setMrno(''); setError(''); }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                    Back
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {patientList.map((patient, idx) => (
                                    <div key={patient.mrno + idx} className="border rounded-lg p-4 flex items-center gap-4 hover:shadow-lg transition cursor-pointer" onClick={async () => {
                                        // First check visit details
                                        try {
                                            const visitResponse = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(patient.mrno)}`, {
                                                headers: {
                                                    Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                                                },
                                            });

                                            if (visitResponse.data && visitResponse.data.status === 'success' && visitResponse.data.data && visitResponse.data.data[0]) {
                                                const visitData = visitResponse.data.data[0].visitDetails;
                                                setVisitDetails(visitData);

                                                // Check if patient is IP with open visit status
                                                if (visitData && visitData.patVisitStatus && visitData.patVisitStatus.startsWith('IP') && visitData.visitStatus === 'open') {
                                                    setIsBlockedIPPatient(true);
                                                    setShowIPStatusDialog(true);
                                                    return; // Don't set patient data
                                                }
                                            }

                                            // If not blocked, set patient data
                                            setPatientData({
                                                prefix: patient.prefix || '',
                                                firstName: patient.firstName || '',
                                                middleName: patient.middleName || '',
                                                lastName: patient.lastName || '',
                                                mrno: patient.mrno || '',
                                                genderCode: patient.genderCode || '',
                                                dob: patient.dobStr ? patient.dobStr.split('-').reverse().join('-') : '',
                                                age: patient.age || '',
                                                ageSelect: 'year',
                                                contactNumber: patient.contactNo || '',
                                                emailAddress: patient.email || '',
                                                emergencyContactName: patient.emergencyContactName || '',
                                                emergencyContactNo: patient.emergencyContactNo || patient.contactNo || '',
                                                city: patient.addressList?.[0]?.cityName || '',
                                                district: patient.addressList?.[0]?.districtName || '',
                                                state: patient.addressList?.[0]?.stateName || '',
                                                nationality: patient.addressList?.[0]?.countryName || '',
                                                addressList: patient.addressList || [],
                                                base64String: patient.photo || null
                                            });
                                            setPatientList([]);
                                            setIsBlockedIPPatient(false);
                                        } catch (err) {
                                            console.error('Error checking visit details:', err);
                                        }
                                    }}>
                                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center">
                                            {patient.photo ? (
                                                <img src={patient.photo} alt="Patient" className="w-full h-full object-cover rounded-xl" />
                                            ) : (
                                                <User className="w-8 h-8 text-emerald-600" />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="text-base font-semibold text-gray-900">
                                                {`${patient.prefix || ''} ${patient.firstName} ${patient.middleName || ''} ${patient.lastName}`.trim()}
                                            </h3>
                                            <p className="text-xs text-gray-500">MRNO: {patient.mrno}</p>
                                            <p className="text-xs text-gray-500">Mobile: {patient.contactNo}</p>
                                            <p className="text-xs text-gray-500">DOB: {patient.dobStr ? patient.dobStr.split('-').reverse().join('-') : ''}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {patientData && (
                        <div className="flex flex-col lg:flex-row gap-6">
                            <div className="lg:w-[70%]">
                                <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-6">
                                    <div className="bg-emerald-50 border-b border-emerald-100 px-6 py-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <CheckCircle className="w-5 h-5 text-emerald-600 mr-3" />
                                                <p className="text-emerald-800 font-medium">Patient Details Retrieved Successfully</p>
                                            </div>
                                            <button
                                                onClick={() => { setPatientData(null); setError(''); setMrno(''); }}
                                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                            >
                                                Re-enter MRN / Mobile Number
                                            </button>
                                        </div>
                                    </div>
                                    <div className="p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="flex items-center space-x-4">
                                                <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center">
                                                    {patientData.base64String ? (
                                                        <img
                                                            src={patientData.base64String}
                                                            alt="Patient"
                                                            className="w-full h-full object-cover rounded-xl"
                                                        />
                                                    ) : (
                                                        <User className="w-8 h-8 text-emerald-600" />
                                                    )}
                                                </div>
                                                <div>
                                                    <h3 className="text-base font-semibold text-gray-900">
                                                        {`${patientData.prefix || ''} ${patientData.firstName} ${patientData.middleName || ''} ${patientData.lastName}`.trim()}
                                                    </h3>
                                                    <p className="text-xs text-gray-500">MRNO: {patientData.mrno}</p>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex items-center">
                                                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                                    <div>
                                                        <p className="text-xs text-gray-500">DOB / Age</p>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {patientData.dob} ({patientData.age} {patientData.ageSelect || 'year'}(s))
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <Phone className="w-4 h-4 text-gray-400 mr-2" />
                                                    <div>
                                                        <p className="text-xs text-gray-500">Mobile</p>
                                                        <p className="text-sm font-medium text-gray-900">{patientData.contactNumber}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex items-center">
                                                    <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                                                    <div>
                                                        <p className="text-xs text-gray-500">Address</p>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {`${patientData.addressList?.[0]?.address || ''}, ${patientData.city}, ${patientData.state}`.trim()}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <Users className="w-4 h-4 text-gray-400 mr-2" />
                                                    <div>
                                                        <p className="text-xs text-gray-500">Emergency Contact</p>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {patientData.emergencyContactName} ({patientData.emergencyContactNo})
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl shadow-sm border p-6">
                                    <div className="flex border-b border-gray-200 mb-4">
                                        {['all', /* 'bed', */ 'lab', 'imaging', 'ot'].map(tab => (
                                            <button
                                                key={tab}
                                                onClick={() => handleTabChange(tab)}
                                                className={`px-4 py-2 text-sm font-medium uppercase ${activeTab === tab
                                                    ? 'border-b-2 border-emerald-600 text-emerald-600'
                                                    : 'text-gray-500 hover:text-emerald-600'
                                                    }`}
                                            >
                                                {tab === 'ot' ? 'PROCEDURE/OT' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-4 mb-4">
                                        <div className="flex-1">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    id="serviceSearch"
                                                    value={searchTerm}
                                                    onChange={handleSearchChange}
                                                    placeholder="Search for services..."
                                                    className="w-full px-3 py-2 border-2 rounded-lg transition-colors duration-200 border-gray-200 focus:border-emerald-500 focus:ring-emerald-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                                                />
                                                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[50vh] overflow-y-auto">
                                        {searchLoading ? (
                                            <div className="col-span-2 flex justify-center items-center">
                                                <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        ) : displayedServices.length === 0 ? (
                                            <p className="col-span-2 text-center text-gray-500">No services found</p>
                                        ) : (
                                            displayedServices.map(service => (
                                                <div
                                                    key={service.serviceId}
                                                    className="p-4 border rounded-lg hover:bg-emerald-50 transition-colors duration-200"
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">{service.serviceName}</p>
                                                            <p className="text-xs text-gray-500">{service.category || service.serviceGroup}</p>
                                                            <p className="text-sm font-semibold text-emerald-600">₹{service.hospitalTariff}</p>
                                                        </div>
                                                        <button
                                                            onClick={() => addToCart(service)}
                                                            className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors duration-200"
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="lg:w-[30%]">
                                <div className="bg-white rounded-xl shadow-sm border p-6 sticky top-6">
                                    {cart.length === 0 ? (
                                        <p className="text-sm text-gray-500">No services added</p>
                                    ) : (
                                        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                                            {cart.map(item => (
                                                <div key={item.serviceId} className="flex justify-between items-center border-b pb-2">
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">{item.serviceName}</p>
                                                        <p className="text-xs text-gray-500">{item.category || item.serviceGroup}</p>
                                                        <div className="flex items-center mt-1">
                                                            <button
                                                                onClick={() => updateQuantity(item.serviceId, -1)}
                                                                className="px-2 py-1 bg-gray-200 rounded-l-md"
                                                            >
                                                                -
                                                            </button>
                                                            <span className="px-3 py-1 bg-gray-100">{item.quantity}</span>
                                                            <button
                                                                onClick={() => updateQuantity(item.serviceId, 1)}
                                                                className="px-2 py-1 bg-gray-200 rounded-r-md"
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-sm font-semibold text-emerald-600">
                                                            ₹{item.hospitalTariff} x {item.quantity}
                                                        </p>
                                                        <p className="text-sm font-semibold text-emerald-600">
                                                            ₹{(item.hospitalTariff * item.quantity).toFixed(2)}
                                                        </p>
                                                        <button
                                                            onClick={() => removeFromCart(item.serviceId)}
                                                            className="text-xs text-red-500 hover:text-red-700"
                                                        >
                                                            <Trash2 className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="border-t pt-4 mt-4">
                                                <div className="flex justify-between items-center">
                                                    <p className="text-sm font-semibold text-gray-900">Total Amount</p>
                                                    <p className="text-lg font-bold text-emerald-600">₹{totalAmount.toFixed(2)}</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={handlePayment}
                                                className={`w-full mt-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg shadow hover:bg-emerald-700 transition duration-200 ${cart.length === 0 || loading ? 'opacity-50 cursor-not-allowed' : ''
                                                    }`}
                                                disabled={cart.length === 0 || loading}
                                            >
                                                {loading ? (
                                                    <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                                                ) : (
                                                    'Proceed to Payment'
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {renderPaymentStatusModal()}
                </div>
            </div>

            {/* IP Status Dialog */}
            {showIPStatusDialog && (
                <div className="fixed inset-0 z-50 flex justify-center items-center w-full h-full bg-black bg-opacity-50">
                    <div className="relative p-8 w-full max-w-md bg-white rounded-2xl shadow-2xl">
                        <div className="text-center">
                            <div className="w-20 h-20 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                                <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-[#22292A] mb-4">
                                Cannot Process Payment
                            </h3>
                            <p className="text-gray-600 text-lg leading-relaxed mb-4">
                                You cannot make online payment because you are currently admitted as an <strong className="text-red-600">In-Patient (IP)</strong> with an <strong className="text-red-600">open</strong> visit status.
                            </p>
                            <div className="bg-red-50 rounded-lg p-4 mb-6">
                                <p className="text-sm text-gray-700">
                                    <strong>Patient Visit Status:</strong> {visitDetails?.patVisitStatus}<br />
                                    <strong>Visit Status:</strong> {visitDetails?.visitStatus}<br />
                                    <strong>Last Visit:</strong> {visitDetails?.lastVisitNo}<br />
                                    <strong>Visit Date:</strong> {visitDetails?.visitDate}
                                </p>
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => setShowIPStatusDialog(false)}
                                    className="flex-1 bg-gray-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-600 transition duration-200"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => navigate('/')}
                                    className="flex-1 bg-[#0FA968] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#138F5E] transition duration-200"
                                >
                                    Go to Home
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OnlinePayment;